import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { Box, Typography } from '@mui/material';
import { differenceInDays, isBefore, parseISO } from 'date-fns';
import { adminTypes } from 'helper/constant';
import { checkAdminAccess } from 'helper/utility';

import { paymentMethodsAction } from 'redux/actions/billing';
import { selectAuthToken, selectCompanyUUID } from 'redux/selectors/auth';

import styles from './billingMovedBanner.module.css';

import { ReactComponent as InfoIcon } from 'asset/IconsPack/hx_info-circle-filled.svg';

const BANNER_FIRST_SEEN_KEY = 'billing_moved_banner_first_seen';
const BANNER_VISIBLE_DAYS = 30;

const { COMPANY_OWNER } = adminTypes;

function BillingMovedBanner() {
    const dispatch = useDispatch();

    const loggedInAdmin = useSelector((state) => state?.auth?.userData?.user);
    const paymentMethods = useSelector((state) => state.billing?.paymentMethods);
    const companyCreatedAt = useSelector(
        (state) => state?.auth?.userData?.user?.company?.created_at
    );
    const token = useSelector(selectAuthToken);
    const companyUUID = useSelector(selectCompanyUUID);

    const [isVisible, setIsVisible] = useState(false);

    const isOwner = checkAdminAccess(loggedInAdmin?.type, [COMPANY_OWNER]);

    useEffect(() => {
        if (!isOwner || !token || !companyUUID) return;
        if (paymentMethods === undefined) {
            // On home page payment methods will be not reflected so need to fetch to get the latest payment method info
            dispatch(
                paymentMethodsAction({ companyAccessCode: companyUUID }, { loader: false }, token)
            );
        }
    }, [isOwner, token, companyUUID, paymentMethods, dispatch]);

    // Note: For the accounts created after the 1 jun 2026 we won't show the banner
    const BILLING_MIGRATION_DATE = new Date('2026-06-01');

    useEffect(() => {
        if (!isOwner || !paymentMethods || paymentMethods.length === 0) return;
        if (companyCreatedAt && !isBefore(parseISO(companyCreatedAt), BILLING_MIGRATION_DATE))
            return;

        const stored = localStorage.getItem(BANNER_FIRST_SEEN_KEY);

        if (stored) {
            const daysSince = differenceInDays(new Date(), parseISO(stored));
            if (daysSince < BANNER_VISIBLE_DAYS) {
                setIsVisible(true);
            }
        } else {
            localStorage.setItem(BANNER_FIRST_SEEN_KEY, new Date().toISOString());
            setIsVisible(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOwner, paymentMethods, companyCreatedAt]);

    if (!isVisible) return null;

    return (
        <Box className={styles.banner}>
            <InfoIcon className={styles.bannerIcon} />
            <Typography className={styles.bannerText}>
                Billing has moved to Settings.{' '}
                <Link to="/settings#billing" className={styles.bannerLink}>
                    Go to Settings
                </Link>{' '}
                to manage billing.
            </Typography>
        </Box>
    );
}

export default BillingMovedBanner;
